import { useSyncExternalStore } from 'react';
import { lazyWithReload } from './lazyWithReload';

/**
 * Route-chunk loading, tracked so the app can show a progress bar.
 *
 * The first time a page is opened its code has to download. The layouts show
 * "Loading page..." in the content area meanwhile (the nav stays); on a slow
 * connection that wait can be long, so a thin bar across the top of the window
 * also shows that something is happening. Every route component is created
 * with `lazyRoute()`, which counts its download here, and RouteProgressBar
 * shows the bar while any are in flight.
 */
let pending = 0;
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());

function trackRouteLoad(promise) {
  pending += 1;
  // React starts a lazy download DURING render. Notifying subscribers
  // synchronously would update RouteProgressBar while another component is
  // rendering, which React forbids — so tell them a microtask later.
  queueMicrotask(emit);

  const settle = () => {
    pending -= 1;
    emit();
  };
  promise.then(settle, settle);

  return promise;
}

/**
 * `lazyWithReload` for route pages: deploy-safe (a stale chunk reloads the
 * app once) and tracked for the progress bar. Use it for route components
 * only — a chart or dialog loading inside a page is not a navigation.
 */
export const lazyRoute = (factory) => lazyWithReload(() => trackRouteLoad(factory()));

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** True while any route chunk is downloading. */
export function useRouteLoading() {
  return useSyncExternalStore(subscribe, () => pending > 0, () => false);
}
